import { AttributesTypes, Block, PdpContentResponse, Product } from "@/common/lib/types";
import { getImageIndex } from "@/apiUtils/productUtils";
import { getFieldValue, renderCurrencyFontIconsInHTML } from "./common";
import {
  AttributeInputTypeEnum,
  SaleorAttributes,
  saleorBlockType,
  PDP_ATTRIBUTE_LABELS,
  LENS_FEATURES_ATTRIBUTES,
} from "@/apiUtils/constants";

type LangTypes = "en" | "ar";

export function extractValueBySlug(lang: LangTypes, attributes: AttributesTypes[], slug: string) {
  for (const attribute of attributes) {
    if (attribute.attribute.slug === slug) {
      // Check for the slug
      if (
        attribute.attribute.inputType === AttributeInputTypeEnum.MULTISELECT &&
        attribute.values.length > 0
      ) {
        const values = attribute.values
          .map((v) => {
            if (v?.name) {
              return getFieldValue(lang, v.name, v.translation?.name ?? null);
            }
            return null;
          })
          .filter((s): s is string => typeof s === "string" && s.trim() !== "" && s !== "-");
        return values.length > 0 ? values.join(", ") : null;
      }

      const value = attribute.values[0];

      // Check for the value in the most likely fields
      if (value?.scoppable) {
        const val = getFieldValue(lang, value.scoppable, value.translation?.scoppable ?? null);
        const json = parseScoppableJson(val);
        return json;
      } else if (value?.plainText) {
        return getFieldValue(lang, value?.plainText, value.translation?.plainText ?? null);
      } else if (value?.richText) {
        return getFieldValue(lang, value?.richText, value.translation?.richText ?? null);
      } else if (value?.boolean !== undefined && value.boolean !== null) {
        return value.boolean.toString();
      } else if (value?.referenceEntity) {
        const val = getFieldValue(
          lang,
          value?.referenceEntity,
          value.translation?.referenceEntity ?? null,
        );
        return val;
      } else if (value?.name) {
        return getFieldValue(lang, value.name, value.translation?.name ?? null);
      }
    }
  }
  return null;
}

export function extractMultiValueBySlug(
  lang: LangTypes,
  attributes: AttributesTypes[],
  slug: string,
): string[] {
  for (const attribute of attributes) {
    if (attribute.attribute.slug === slug) {
      // Handle multi-value attributes (like multi-select reference entities)
      const values: string[] = [];

      for (const value of attribute.values) {
        if (value?.referenceEntity) {
          const val = getFieldValue(
            lang,
            value?.referenceEntity,
            value.translation?.referenceEntity ?? null,
          );
          if (val) values.push(val);
        } else if (value?.name) {
          const val = getFieldValue(lang, value.name, value.translation?.name ?? null);
          if (val) values.push(val);
        } else if (value?.plainText) {
          const val = getFieldValue(lang, value?.plainText, value.translation?.plainText ?? null);
          if (val) values.push(val);
        }
      }

      return values;
    }
  }
  return [];
}

function parseScoppableJson(data: string | null | undefined) {
  const BRAND_ID = process.env.NEXT_PUBLIC_BRAND_ID;
  const FALLBACK_BRAND_ID = process.env.NEXT_PUBLIC_FALLBACK_BRAND_ID;
  if (!BRAND_ID) throw Error("BRAND_ID not found");
  const json = JSON.parse(data ?? "{}");
  return json[BRAND_ID] ?? json[FALLBACK_BRAND_ID!];
}

export function parseJsonGetValue(jsonObj: any, keyPath: string): any | undefined {
  if (jsonObj === "") jsonObj = null;

  const keys = keyPath.split(".");
  let value: any = JSON.parse(jsonObj);

  for (const key of keys) {
    if (value !== null && typeof value === "object" && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }
  return value;
}

// Returns undefined rather than [] so callers' `??` fallback chain still works.
const splitAndSortImages = (images: string | null | undefined): string[] | undefined => {
  const list = images?.split(",").filter(Boolean) ?? [];
  if (list.length === 0) return undefined;
  return list.sort((a, b) => getImageIndex(a) - getImageIndex(b));
};

export const getProductImages = (lang: LangTypes, attributes: AttributesTypes[], slug: string) => {
  return splitAndSortImages(extractValueBySlug(lang, attributes, slug) as string | null);
};

export const getClProductImages = (lang: LangTypes, attributes: AttributesTypes[]) => {
  return splitAndSortImages(extractValueBySlug(lang, attributes, "cl_product_image") as string | null);
};

// export const getProductInfo = (lang: LangTypes, product: Product, variantIdx: number) => {
//   if (product?.variants == undefined) throw new Error("Product varaint not found");

//   return {
//     model: extractValueBySlug(lang, product?.variants?.[variantIdx!].attributes ?? [], "model"),
//     "color code": extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "colorCode",
//     ),
//     ean: extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "sapEAN"),
//     shape: parseJsonGetValue(
//       extractValueBySlug(lang, product?.attributes ?? [], "shape"),
//       "attributes.label",
//     ),
//     "frame material": parseJsonGetValue(
//       extractValueBySlug(lang, product?.attributes ?? [], "frameMaterialEnum"),
//       "attributes.label",
//     ),
//     "frame color": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "fcCodeEnum"),
//       "attributes.label",
//     ),
//     "lens color": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "lenseColor"),
//       "attributes.label",
//     ),
//     polarized: extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "polarized",
//     ),
//     mirrored: extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "mirror"),
//     "CL Material": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clMatEnum"),
//       "attributes.label",
//     ),
//     "CL Color": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clColorEnum"),
//       "attributes.label",
//     ),
//     "Water Content": extractValueBySlug(lang, product?.attributes ?? [], "clWater"),
//     Diameter: extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "clDiameter",
//     ),
//     "Base Curve": extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "clBaseCurve",
//     ),
//     "UV Block": extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "uvBlock",
//     ),
//     Usage: extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clUsage"),
//   };
// };

export const extractProductDetails = (
  productType: string,
  lang: "en" | "ar",
  attributes: Record<string, ExtractedAttribute>,
  layoutDetails: PdpContentResponse,
): Array<{
  label: string | undefined;
  value: string | undefined;
}> => {
  const getMeasurements = () => {
    const sizes = [
      attributes?.[SaleorAttributes.LENS_SIZE]?.value,
      attributes[SaleorAttributes.BRIDGE_SIZE]?.value,
      attributes[SaleorAttributes.TEMPLE_SIZE]?.value,
    ];
    const exists = sizes.some((v) => v !== undefined);
    if (!exists) return undefined;
    return sizes?.map((v) => formatMeasurement(v, lang)).join(" | ");
  };

  // Helper function to get lens features (matching storefront implementation)
  const getLensFeatures = () => {
    // First, check if there's a multi-select "lens_features" attribute from Saleor
    const multiSelectLensFeatures = attributes?.["lens_features"]?.value;

    if (
      multiSelectLensFeatures &&
      typeof multiSelectLensFeatures === "string" &&
      multiSelectLensFeatures.trim()
    ) {
      // If multi-select attribute exists and has values, use it directly
      return multiSelectLensFeatures;
    }

    // Fallback: Combine individual boolean attributes (backward compatibility)
    const activeFeatures: string[] = [];

    LENS_FEATURES_ATTRIBUTES.forEach((attrSlug) => {
      const attr = attributes?.[attrSlug];

      if (attr?.type === AttributeInputTypeEnum.BOOLEAN && attr?.value === "true") {
        const featureName = attr?.attribute ? removeCLPrefix(attr.attribute) : null;
        if (featureName) {
          activeFeatures.push(featureName);
        }
      }
    });

    return activeFeatures.length > 0 ? activeFeatures.join(", ") : null;
  };

  let slugs = [
    "model",
    "colorCode",
    "sapEAN",
    "shape",
    "frameMaterialEnum",
    "fcCodeEnum",
    "lenseColor",
    "clMatEnum",
    "clColorEnum",
    "clWater",
    "clDiameter",
    "clBaseCurve",
    "uvBlock",
    "clUsage",
    "acModel",
    "temp_clipon_style",
    "accColorFamilyEnumCode",
    "acGenderEnumCode",
    "acPrdCatgEnumCode",
    "acPrdMatEnumCode",
    "geo_fit",
    "frame_lens_material",
    "clipon_framecolor",
    "clipon_lenscolor",
    "clipon_style",
    "uv_block",
    "material_composition",
    "frame_finish",
    "clip_on_1",
    "clip_on_2",
    "frame_pattern",
    "expandable",
    "armless",
    "temple_material",
    "temple_tip_material",
    "temple_color",
    "temple_tip_color",
    "hinges_material",
    "oxygen_permeability",
    "manufacturer",
    "frame_design",
    "theme",
    "collectionName",
  ];

  if (productType === "eyeglasses") {
    slugs = slugs.filter((slug) => slug !== "lenseColor");
  }

  const formatFields = ["clWater", "clDiameter", "clBaseCurve", "oxygen_permeability"];
  const acc = [];

  for (let key of slugs) {
    const attrExists = attributes?.[key] !== undefined;

    if (!attrExists) {
      continue; // Skip this attribute if it doesn't exist
    }

    // Skip lens feature boolean attributes - they'll be grouped separately
    if (LENS_FEATURES_ATTRIBUTES.includes(key)) {
      continue; // Skip adding to main array
    }

    const label = attributes?.[key]?.attribute
      ? removeCLPrefix(attributes?.[key]?.attribute!)
      : attributes?.[key]?.attribute;
    let value;

    if (attributes?.[key]?.type === AttributeInputTypeEnum.BOOLEAN) {
      // Only show "Yes" for boolean attributes, skip "No" values
      if (attributes?.[key]?.value === "false") {
        continue; // Skip this attribute entirely
      }
      value = getFieldValue(lang, "Yes", layoutDetails.translations.pdp?.pdp_attribute_value_yes);
    } else if (formatFields.includes(attributes?.[key]?.slug!)) {
      value = formatMeasurement(attributes?.[key]?.value, lang);
    } else if (attributes?.[key]?.type === AttributeInputTypeEnum.REFERENCE_ENTITY) {
      value = isJson(attributes?.[key]?.value)
        ? parseJsonGetValue(attributes?.[key]?.value, "attributes.label")
        : attributes?.[key]?.value;
    } else {
      value = attributes?.[key]?.value;
    }

    // Skip if both label and value are undefined or empty
    if (!label || !value) {
      continue;
    }

    acc.push({ label, value });
  }

  // Add grouped lens features if any exist
  const lensFeatures = getLensFeatures();
  if (lensFeatures) {
    acc.push({
      label:
        layoutDetails.translations?.pdp?.pdp_product_details_tab_lens_features_label ||
        PDP_ATTRIBUTE_LABELS.LENS_FEATURES[lang],
      value: lensFeatures,
    });
  }

  acc.push({
    label: layoutDetails.translations?.pdp?.pdp_attribute_measurements,
    value: getMeasurements(),
  });

  return acc;
};

// export const getClProductInfo = (lang: LangTypes, product: Product, variantIdx: number) => {
//   if (product?.variants == undefined) throw new Error("Product varaint not found");

//   return {
//     "CL Material": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clMatEnum"),
//       "attributes.label",
//     ),
//     "CL Color": parseJsonGetValue(
//       extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clColorEnum"),
//       "attributes.label",
//     ),
//     "Water Content": extractValueBySlug(lang, product?.attributes ?? [], "clWater"),
//     Diameter: extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "clDiameter",
//     ),
//     "Base Curve": extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "clBaseCurve",
//     ),
//     "UV Block": extractValueBySlug(
//       lang,
//       product?.variants[variantIdx!].attributes ?? [],
//       "uvBlock",
//     ),
//     Usage: extractValueBySlug(lang, product?.variants[variantIdx!].attributes ?? [], "clUsage"),
//   };
// };

export interface ExtractedAttribute {
  attribute: string | undefined;
  type: string | undefined;
  slug: string | undefined;
  value: any;
  originalValue: any;
}

export const extractVariantAttributes = (
  lang: LangTypes,
  product: Product,
  variantIdx: number,
): ExtractedAttribute[] => {
  const allAttributes: ExtractedAttribute[] = [];

  if (product.variants === undefined) {
    return [];
  }

  // Process product-level attributes
  for (let i = 0; i < product.attributes.length; i++) {
    const parseAbleField = [
      "shape",
      "parent_brand",
      "frameTypeEnum",
      "frameMaterialEnum",
      "clipon_style",
      "pair_swatch_image",
    ];
    const toParse = parseAbleField.includes(product.attributes[i].attribute.slug);

    const type = product.attributes[i].attribute.inputType;
    const slug = product.attributes[i].attribute.slug;
    const attribute = getFieldValue(
      lang,
      product.attributes[i].attribute.name,
      product.attributes[i].attribute.translation?.name,
    );

    let value;
    let originalValue;

    // Handle multi-select reference entity (like lens_features)
    if (type === "REFERENCE_ENTITY" && product.attributes[i].values.length > 1) {
      const multiValues = extractMultiValueBySlug(lang, [product.attributes[i]], slug);
      value = multiValues.join(", ");

      const multiValuesOriginal = extractMultiValueBySlug("en", [product.attributes[i]], slug);
      originalValue = multiValuesOriginal.join(", ");
    } else {
      value = toParse
        ? parseJsonGetValue(
            extractValueBySlug(lang, [product.attributes[i]], slug),
            "attributes.label",
          )
        : extractValueBySlug(lang, [product.attributes[i]], slug);

      originalValue = toParse
        ? parseJsonGetValue(
            extractValueBySlug("en", [product.attributes[i]], slug),
            "attributes.label",
          )
        : extractValueBySlug("en", [product.attributes[i]], slug);
    }

    allAttributes.push({ attribute, type, slug, value, originalValue });
  }

  // Process variant-level attributes
  for (let i = 0; i < product.variants[variantIdx].attributes.length; i++) {
    if (product.variants[variantIdx].attributes[i]?.attribute === undefined) continue;

    const pImgregex = /_image/gi;
    const type = product.variants[variantIdx].attributes[i].attribute.inputType;
    const slug = product.variants[variantIdx].attributes[i].attribute.slug;
    const isImage = pImgregex.test(slug);
    const attribute = getFieldValue(
      lang,
      product.variants[variantIdx].attributes[i]?.attribute.name,
      product.variants[variantIdx].attributes[i]?.attribute.translation?.name,
    );
    let value;
    let originalValue;

    if (type === "SCOPPABLE" && isImage) {
      value = getProductImages(lang, [product.variants[variantIdx].attributes[i]], slug);
    }
    // Handle multi-select reference entity (like lens_features)
    else if (
      type === "REFERENCE_ENTITY" &&
      product.variants[variantIdx].attributes[i].values.length > 1
    ) {
      const multiValues = extractMultiValueBySlug(
        lang,
        [product.variants[variantIdx].attributes[i]],
        slug,
      );
      value = multiValues.join(", ");

      const multiValuesOriginal = extractMultiValueBySlug(
        "en",
        [product.variants[variantIdx].attributes[i]],
        slug,
      );
      originalValue = multiValuesOriginal.join(", ");
    }
    // Handle single-value reference entity
    else if (type === "REFERENCE_ENTITY") {
      value = parseJsonGetValue(
        extractValueBySlug(
          lang,
          [product.variants[variantIdx].attributes[i]],
          product.variants[variantIdx].attributes[i].attribute.slug,
        ),
        "attributes.label",
      );

      originalValue = parseJsonGetValue(
        extractValueBySlug(
          "en",
          [product.variants[variantIdx].attributes[i]],
          product.variants[variantIdx].attributes[i].attribute.slug,
        ),
        "attributes.label",
      );
    } else {
      value = extractValueBySlug(
        lang,
        [product.variants[variantIdx].attributes[i]],
        product.variants[variantIdx].attributes[i].attribute.slug,
      );
      originalValue = extractValueBySlug(
        "en",
        [product.variants[variantIdx].attributes[i]],
        product.variants[variantIdx].attributes[i].attribute.slug,
      );
    }

    allAttributes.push({ attribute, type, slug, value, originalValue });
  }

  return allAttributes;
};

export const extractBundleVariantAttributes = (
  lang: LangTypes,
  productAttributes: AttributesTypes[],
  variantAttributes: AttributesTypes[],
): ExtractedAttribute[] => {
  const allAttributes: ExtractedAttribute[] = [];

  for (let i = 0; i < productAttributes.length; i++) {
    const parseAbleField = [
      "shape",
      "parent_brand",
      "frameTypeEnum",
      "frameMaterialEnum",
      "clipon_style",
    ];
    const toParse = parseAbleField.includes(productAttributes[i].attribute.slug);

    const type = productAttributes[i].attribute.inputType;
    const slug = productAttributes[i].attribute.slug;
    const attribute = getFieldValue(
      lang,
      productAttributes[i].attribute.name,
      productAttributes[i].attribute.translation?.name,
    );
    const value = toParse
      ? parseJsonGetValue(
          extractValueBySlug(lang, [productAttributes[i]], slug),
          "attributes.label",
        )
      : extractValueBySlug(lang, [productAttributes[i]], slug);

    const originalValue = toParse
      ? parseJsonGetValue(
          extractValueBySlug("en", [productAttributes[i]], slug),
          "attributes.label",
        )
      : extractValueBySlug("en", [productAttributes[i]], slug);

    allAttributes.push({ attribute, type, slug, value, originalValue });
  }

  for (let i = 0; i < variantAttributes.length; i++) {
    if (variantAttributes[i]?.attribute === undefined) continue;

    const pImgregex = /_image/gi;
    const type = variantAttributes[i].attribute.inputType;
    const slug = variantAttributes[i].attribute.slug;
    const isImage = pImgregex.test(slug);
    const attribute = getFieldValue(
      lang,
      variantAttributes[i]?.attribute.name,
      variantAttributes[i]?.attribute.translation?.name,
    );
    let value;
    let originalValue;

    if (type === "SCOPPABLE" && isImage) {
      value = getProductImages(lang, [variantAttributes[i]], slug);
    }
    else if (type === "REFERENCE_ENTITY") {
      value = parseJsonGetValue(
        extractValueBySlug(
          lang,
          [variantAttributes[i]],
          variantAttributes[i].attribute.slug,
        ),
        "attributes.label",
      );

      originalValue = parseJsonGetValue(
        extractValueBySlug(
          "en",
          [variantAttributes[i]],
          variantAttributes[i].attribute.slug,
        ),
        "attributes.label",
      );
    } else {
      value = extractValueBySlug(
        lang,
        [variantAttributes[i]],
        variantAttributes[i].attribute.slug,
      );
      originalValue = extractValueBySlug(
        "en",
        [variantAttributes[i]],
        variantAttributes[i].attribute.slug,
      );
    }

    allAttributes.push({ attribute, type, slug, value, originalValue });
  }

  return allAttributes;
};

export const getRequiredAttributes = (attributes: ExtractedAttribute[], slugs: string[]) => {
  const obj: Record<string, ExtractedAttribute> = {};
  const attr = attributes.filter((attr) => slugs.includes(attr.slug!));

  for (let i = 0; i < attr.length; i++) {
    obj[attr[i].slug!] = attr[i];
  }
  return obj;
};

export const isNumber = (s: string): boolean => {
  return !isNaN(parseFloat(s)) && isFinite(Number(s));
};

export function formatMeasurement(str: string, lang: "en" | "ar"): string | undefined {
  try {
    const [value, unit] = str.split(" ");
    if (!value) return undefined;
    const res = `${Number(value).toString()} ${unit ? unit?.replace(/MILLIMETER/g, lang === "ar" ? "ملم" : "mm") : ""}`;
    return res;
  } catch (err) {
    return undefined;
  }
}

export const removeCLPrefix = (input: string): string => {
  return input.replace(/\bCL\s/g, "");
};

export function convertToHTML(blocks: Block[]): string {
  return blocks
    ?.map((block: Block) => {
      switch (block.type) {
        case saleorBlockType.PARAGRAPH:
          return `<p>${renderCurrencyFontIconsInHTML(block.data.text)}</p>`;
        case saleorBlockType.HEADER:
          return `<h${block.data.level}>${renderCurrencyFontIconsInHTML(block.data.text)}</h${block.data.level}>`;
        case saleorBlockType.LIST: {
          const listItems: string = block.data.items
            ?.map((item: string) => `<li>${renderCurrencyFontIconsInHTML(item)}</li>`)
            .join("");
          const listType: "ol" | "ul" = block.data.style === "ordered" ? "ol" : "ul";
          return `<${listType}>${listItems}</${listType}>`;
        }
        case saleorBlockType.QUOTE:
          return `<blockquote>${renderCurrencyFontIconsInHTML(block.data.text)}<br/><cite>${renderCurrencyFontIconsInHTML(block.data.caption)}</cite></blockquote>`;
        default:
          // Handle other block types if needed
          return "";
      }
    })
    .join("");
}

export interface IPowerList {
  id: string;
  url: string;
  power: string;
  quantity: number;
  color: string;
  clPerPackSize: string;
}
export function getPowerList(data: IPowerList[]) {
  const powers = data
    ?.filter((v) => v.power !== undefined)
    ?.map((item) => {
      let [num, sentiment] = item?.power?.split(" ") || [];
      let convertedValue;

      if (sentiment?.toLowerCase() === "negative") {
        convertedValue = -parseFloat(num);
      } else {
        convertedValue = parseFloat(num);
      }

      return {
        ...item,
        power: convertedValue,
      };
    });

  return powers
    .sort((a, b) => a.power - b.power)
    ?.map((v) => {
      return { ...v, power: parseFloat(v.power + "").toFixed(2) };
    });
}

export function isContactLensInStock(powerList: Record<string, IPowerList[]> | undefined): boolean {
  if (!powerList || Object.keys(powerList).length === 0) {
    return false;
  }

  for (const colorPowers of Object.values(powerList)) {
    for (const powerVariant of colorPowers) {
      if (powerVariant.quantity > 0) {
        return true;
      }
    }
  }

  return false;
}


export function isContactLensColorInStock(
  powerList: Record<string, IPowerList[]> | undefined,
  color: string,
): boolean {
  if (!powerList || !color) {
    return false;
  }

  const colorPowers = powerList[color];
  if (!colorPowers?.length) {
    return false;
  }

  return colorPowers.some((powerVariant) => powerVariant.quantity > 0);
}

export function isJson(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function correctImgUrl(url: string) {
  if (url?.startsWith("files")) return "https://ecom-cdn.magrabi.com/prod/" + url;
  return url;
}

export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function translateType(type: string, lang: "en" | "ar") {
  if (!lang) return type;

  const safeType = type === "model" ? "model" : "product";

  const translations = {
    product: {
      en: "Product Photo",
      ar: "صورة المنتج",
    },
    model: {
      en: "Model Photo",
      ar: "صورة النموذج",
    },
  };

  return translations[safeType][lang];
}

export function generateAltText(
  brand: string,
  productName: string,
  color: string = "",
  swatchesColor: string = "",
  lang: "en" | "ar" = "en",
): string {
  let altText = "";

  // Check if this is a Pair Eyewear product
  const isPairEyewear = brand?.toLowerCase().includes("pair eyewear");
  const hasSwatches = isPairEyewear && swatchesColor && swatchesColor.trim() !== "";

  if (lang === "en") {
    // English format: Shop [Brand] [Product Name] in [Color] online.
    // Pair Eyewear format: Shop [Brand] [Product Name] in [Color] & [Swatches] online.
    altText = "Shop";

    if (brand) {
      altText += ` ${brand}`;
    }

    if (productName) {
      altText += ` ${productName}`;
    }

    if (color) {
      altText += ` in ${color}`;

      // Add swatches color for Pair Eyewear products
      if (hasSwatches) {
        altText += ` & ${swatchesColor}`;
      }
    }

    altText += " online.";
  } else {
    // Arabic format: تسوق [Product Name] [Brand] بلون [Color] أونلاين.
    // Pair Eyewear format: تسوق [Product Name] [Brand] بلون [Product Color] و [Swatches] أونلاين.
    altText = "تسوق";

    if (productName) {
      altText += ` ${productName}`;
    }

    if (brand) {
      altText += ` ${brand}`;
    }

    if (color) {
      altText += ` بلون ${color}`;

      // Add swatches color for Pair Eyewear products
      if (hasSwatches) {
        altText += ` و ${swatchesColor}`;
      }
    }

    altText += " أونلاين.";
  }

  return altText.trim() || "product-image";
}

export function combineAltTextWithImageInfo(
  altText: string,
  imageType: string,
  lang: "en" | "ar" = "en",
  imageIndex?: number,
): string {
  if (!altText) return "product-image";

  const translatedType = translateType(imageType, lang);
  if (!translatedType) return altText;

  let finalAltText = "";

  if (lang === "en") {
    // English format: [Alt Text] – [Image Type] [Index]
    finalAltText = `${altText} – ${translatedType}`;
    if (imageIndex !== undefined && imageIndex !== null) {
      finalAltText += ` ${imageIndex}`;
    }
  } else {
    // Arabic format: [Alt Text] - [Image Type] [Index]
    // For RTL display, we need to make sure the index appears at the end
    // of the string (which will be displayed on the right in RTL)
    finalAltText = `${altText} - ${translatedType}`;
    if (imageIndex !== undefined && imageIndex !== null) {
      finalAltText += ` ${imageIndex}`;
    }
  }

  return finalAltText;
}

export function generateCompleteAltText(
  brand: string,
  productName: string,
  color: string = "",
  lang: "en" | "ar" = "en",
  imageType: string = "product",
  imageIndex?: number,
  swatchesColor: string = "",
): string {
  const baseAltText = generateAltText(brand, productName, color, swatchesColor, lang);
  return combineAltTextWithImageInfo(baseAltText, imageType, lang, imageIndex);
}
